import { format, addDays } from 'date-fns'
import { DayRow } from './DayRow'
import type { TrainingSession } from '../../types/training-session'
import type { Goal } from '../../types/goal'
import type { Workout } from '../../types/workout'
import type { WorkoutResultMatch } from '../../utils/match-workout-results'
import type { DayAttempts, AscentItem } from '../../services/training-diary'
import type { SessionPayload } from './DayRow'

const EMPTY_ATTEMPTS: DayAttempts = { count: 0, items: [] }
const EMPTY_ASCENTS: AscentItem[] = []
const EMPTY_WORKOUTS: Workout[] = []

interface WeekDayListProps {
  weekStart: Date
  sessionsByDate: Record<string, TrainingSession>
  goalsByDate: Record<string, Goal[]>
  workoutsByDate: Record<string, Workout[]>
  workoutMatches: Map<string, WorkoutResultMatch>
  attemptsByDate: Record<string, DayAttempts>
  ascentsByDate: Record<string, AscentItem[]>
  scheduleMode: boolean
  onSessionCreate: (data: SessionPayload) => Promise<void>
  onSessionUpdate: (id: string, data: SessionPayload) => Promise<void>
  onSessionDelete: (id: string) => Promise<void>
  onGoalCreate: (data: { body: string; target_date: string }) => Promise<void>
  onGoalUpdate: (id: string, data: { body: string; target_date: string }) => Promise<void>
  onWorkoutCreate: (date: string, exerciseId: string) => Promise<void>
  onWorkoutDayPick: (workoutId: string, date: string | null) => void
  onWorkoutReorder: (ids: string[]) => void
  /** ISO day whose add-workout dialog should open on mount (dashboard deep link). */
  autoAddWorkoutDate?: string | null
  onAutoAddWorkoutHandled?: () => void
}

export function WeekDayList({
  weekStart,
  sessionsByDate,
  goalsByDate,
  workoutsByDate,
  workoutMatches,
  attemptsByDate,
  ascentsByDate,
  scheduleMode,
  onSessionCreate,
  onSessionUpdate,
  onSessionDelete,
  onGoalCreate,
  onGoalUpdate,
  onWorkoutCreate,
  onWorkoutDayPick,
  onWorkoutReorder,
  autoAddWorkoutDate,
  onAutoAddWorkoutHandled,
}: WeekDayListProps) {
  const weekStartIso = format(weekStart, 'yyyy-MM-dd')
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(weekStart, i)
    return format(d, 'yyyy-MM-dd')
  })

  return (
    <div className="flex flex-col gap-3">
      {days.map((date) => (
        <DayRow
          key={date}
          date={date}
          weekStart={weekStartIso}
          session={sessionsByDate[date] ?? null}
          goals={goalsByDate[date] ?? []}
          workouts={workoutsByDate[date] ?? EMPTY_WORKOUTS}
          workoutMatches={workoutMatches}
          attempts={attemptsByDate[date] ?? EMPTY_ATTEMPTS}
          ascents={ascentsByDate[date] ?? EMPTY_ASCENTS}
          scheduleMode={scheduleMode}
          onSessionCreate={onSessionCreate}
          onSessionUpdate={onSessionUpdate}
          onSessionDelete={onSessionDelete}
          onGoalCreate={onGoalCreate}
          onGoalUpdate={onGoalUpdate}
          onWorkoutCreate={(exerciseId) => onWorkoutCreate(date, exerciseId)}
          onWorkoutDayPick={onWorkoutDayPick}
          onWorkoutReorder={onWorkoutReorder}
          autoOpenAddWorkout={autoAddWorkoutDate === date}
          onAutoOpenAddWorkoutHandled={onAutoAddWorkoutHandled}
        />
      ))}
    </div>
  )
}
